import {
  TOOLBOX_ACCESS_PATH,
  TOOLBOX_ROLES_PATH,
  toolboxAccessResponseSchema,
  toolboxProjectAccessSchema,
  toolboxRoleAssignmentListResponseSchema,
  toolboxRoleAssignmentSchema,
  type CreateRoleAssignmentBody,
  type ToolboxAccessResponse,
  type ToolboxPersona,
  type ToolboxProjectAccess,
  type ToolboxRoleAssignment,
} from "@ttc-spectree/toolbox-contract/roles";
import { api } from "./client";

/**
 * Client for the SpecTree Toolbox access and role API (epic PI-SPEC-SW-E283,
 * feature PI-SPEC-SW-1429):
 * - `GET    /toolbox/access`                  the caller's roles, capabilities and personas per project
 * - `GET    /toolbox/access/granters?project=` who can grant a role (epic PI-SPEC-SW-E288)
 * - `PUT    /toolbox/access/:project/persona` choose the caller's persona on one project
 * - `GET    /toolbox/roles?project=`          role assignments on one project
 * - `POST   /toolbox/roles`                   assign a role
 * - `DELETE /toolbox/roles/:id`               remove a role assignment
 *
 * Responses are parsed with the lenient contract schemas, so a role,
 * capability or persona a newer SpecTree adds is dropped rather than failing
 * the page. The SpecTree API enforces every gate; pages only hide what it
 * would refuse.
 */

interface DataResponse<T> {
  data: T;
}

/** One person who can grant a Toolbox role. */
export interface ToolboxGranter {
  name: string;
  email: string;
}

/**
 * `data` of `GET /toolbox/access/granters?project=` (epic PI-SPEC-SW-E288):
 * the Admins, and the project's Project Managers when a project is given.
 * Any signed-in person may read it, someone with no role included.
 */
export interface ToolboxGranters {
  admins: ToolboxGranter[];
  projectManagers?: ToolboxGranter[];
}

/** The api client's base is `/api/v1`; the contract paths are absolute. */
const API_PREFIX = "/api/v1";

function relative(path: string): string {
  return path.startsWith(API_PREFIX) ? path.slice(API_PREFIX.length) : path;
}

const ACCESS = relative(TOOLBOX_ACCESS_PATH);
const ROLES = relative(TOOLBOX_ROLES_PATH);

export const toolboxAccessApi = {
  /** The caller's access on every project they hold a role on. */
  get: async (): Promise<ToolboxAccessResponse> => {
    const response = await api.get<DataResponse<unknown>>(ACCESS);
    return toolboxAccessResponseSchema.parse(response.data);
  },

  /** Who can grant a role: the Admins, and with `project` that project's Project Managers. */
  getRoleGranters: async (project?: string): Promise<ToolboxGranters> => {
    const query = project ? `?project=${encodeURIComponent(project)}` : "";
    const response = await api.get<DataResponse<ToolboxGranters>>(`${ACCESS}/granters${query}`);
    return response.data;
  },

  /** Choose the persona the tools use for the caller on `project`; returns that project's access. */
  setPersona: async (project: string, persona: ToolboxPersona): Promise<ToolboxProjectAccess> => {
    const response = await api.put<DataResponse<unknown>>(`${ACCESS}/${encodeURIComponent(project)}/persona`, {
      persona,
    });
    return toolboxProjectAccessSchema.parse(response.data);
  },

  /** Role assignments on one project. */
  listRoles: async (project: string): Promise<ToolboxRoleAssignment[]> => {
    const response = await api.get<DataResponse<unknown>>(`${ROLES}?project=${encodeURIComponent(project)}`);
    return toolboxRoleAssignmentListResponseSchema.parse(response.data);
  },

  /** Assign a per-project role; returns the created (or already existing) assignment. */
  assignRole: async (body: CreateRoleAssignmentBody): Promise<ToolboxRoleAssignment> => {
    const response = await api.post<DataResponse<unknown>>(ROLES, body);
    return toolboxRoleAssignmentSchema.parse(response.data);
  },

  /** Remove one role assignment. */
  removeRole: async (assignmentId: string): Promise<void> => {
    await api.delete<unknown>(`${ROLES}/${encodeURIComponent(assignmentId)}`);
  },
};

export type { ToolboxAccessResponse, ToolboxProjectAccess, ToolboxRoleAssignment };
